import { Avatar } from '../components/Cast';
import { Illustration } from '../components/Illustration';
import { StepHead } from '../components/StepHead';
import { ArrowRight, Eye, Headphones, Play, Repeat, Volume2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { player } from '../audio/player';
import type { RecordingResult } from '../audio/recorder';
import { canOfferRecording, micDeclined, noteMicProblem, useMicAccess } from '../audio/useMic';
import { RecordControl } from '../components/RecordControl';
import { GreekText } from '../components/GreekText';
import { InlineGreek } from '../components/InlineGreek';
import { getItem, roleName } from '../content';
import type { Clip, Lesson, SceneTurn, Staff } from '../content/types';
import { barsIn, clipFor, resampleBars, sceneVoice, speechRange } from '../lib/voice';

interface Props {
  lesson: Lesson;
  onDone: () => void;
  onSkip: () => void;
}

/** Thinking time per line in the faster round: as long as the model takes to say it, plus a breath. */
function fastSeconds(turn: SceneTurn, staff: Staff): number {
  if (turn.who !== 'you') return 0;
  const item = getItem(turn.item);
  const clip = clipFor(item, sceneVoice(item, 'you', staff));
  const range = speechRange(clip);
  return Math.round((range.to - range.from + 1.5) * 10) / 10;
}

/** A line heard but not yet read: its shape as a small waveform, so the bubble isn't empty. */
function SpokenLine({ clip }: { clip: Clip }) {
  const bars = resampleBars(barsIn(clip.peaks, clip.duration, speechRange(clip)), 18);
  return (
    <div className="wave wave-mini" aria-hidden="true">
      {bars.map((b, k) => (
        <span key={k} style={{ height: `${Math.max(12, b)}%` }} />
      ))}
    </div>
  );
}

/**
 * The lesson's scene as a role-play: the staff's lines are played, the learner says their own
 * lines aloud and then hears the model. A second, faster round gives a few seconds per line
 * and no English, which builds fluency rather than only accuracy.
 */
export function SceneStep({ lesson, onDone, onSkip }: Props) {
  const turns = lesson.scene.turns;
  const role = roleName(lesson.staff);
  const [round, setRound] = useState<1 | 2>(1);
  const [started, setStarted] = useState(false);
  const [shown, setShown] = useState(0);
  const [revealed, setRevealed] = useState<Set<number>>(new Set());
  const [textShown, setTextShown] = useState<Set<number>>(new Set());
  const [countdown, setCountdown] = useState(false);
  // The learner's own lines, recorded in the first round, for "Hear your conversation" at the end.
  const [takes, setTakes] = useState<Record<number, RecordingResult>>({});
  const takesRef = useRef(takes);
  takesRef.current = takes;
  const access = useMicAccess();
  const [refused, setRefused] = useState(micDeclined);
  const canRecord = canOfferRecording(access, refused);
  const logEnd = useRef<HTMLDivElement>(null);
  const timer = useRef(0);

  useEffect(() => () => {
    clearTimeout(timer.current);
    player.stop();
    for (const take of Object.values(takesRef.current)) URL.revokeObjectURL(take.url);
  }, []);

  // Keep the newest line (and, at the end, the outro) in view above the sticky buttons.
  useEffect(() => {
    logEnd.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [shown, revealed]);

  /** The whole scene once more: the other person's lines and the learner's own recordings, in turn. */
  const playConversation = () => {
    const items = turns.flatMap((turn, i) => {
      if (turn.who === 'narrator') return [];
      const take = turn.who === 'you' ? takes[i] : undefined;
      if (take) return [{ src: take.url, ...take.speech, tag: `scene:${lesson.id}:conversation` }];
      const item = getItem(turn.item);
      const clip = clipFor(item, sceneVoice(item, turn.who, lesson.staff));
      return [{ src: clip.src, ...speechRange(clip), tag: `scene:${lesson.id}:conversation` }];
    });
    player.sequence(items);
  };

  const playTurn = (turn: SceneTurn) => {
    if (turn.who === 'narrator') return;
    const item = getItem(turn.item);
    const clip = clipFor(item, sceneVoice(item, turn.who, lesson.staff));
    const range = speechRange(clip);
    const slow = turn.who === 'staff' && turn.slow;
    player.play(clip.src, { ...range, rate: slow ? 0.75 : 1, tag: `scene:${lesson.id}:${turn.item}` });
  };

  const reveal = (index: number) => {
    clearTimeout(timer.current);
    setCountdown(false);
    setRevealed((r) => new Set(r).add(index));
    playTurn(turns[index]);
  };

  const show = (index: number) => {
    setShown(index + 1);
    const turn = turns[index];
    if (turn.who === 'staff') playTurn(turn);
    if (turn.who === 'you' && round === 2) {
      setCountdown(true);
      timer.current = window.setTimeout(() => reveal(index), fastSeconds(turn, lesson.staff) * 1000);
    }
  };

  const start = () => {
    setStarted(true);
    show(0);
  };

  const current = shown - 1;
  const currentTurn = turns[current];
  const waitingForYou = currentTurn?.who === 'you' && !revealed.has(current);
  const finished = started && shown === turns.length && !waitingForYou;

  const onNext = () => {
    if (waitingForYou) {
      reveal(current);
      return;
    }
    if (shown < turns.length) show(shown);
  };

  const again = () => {
    player.stop();
    setRound(2);
    setShown(0);
    setRevealed(new Set());
    setTextShown(new Set());
    setStarted(false);
  };

  return (
    <div className="step scene-step" data-started={started || undefined}>
      <Illustration id={lesson.id} size="scene" priority decorative className="scene-banner" />
      <div className="stack">
        <StepHead kind="use">
          <span className="chip">Role-play</span>
          {round === 2 && <span className="chip chip-warn">Faster round</span>}
        </StepHead>
        <h2 className="title">{lesson.title}</h2>
        <p className="subtitle">
          <InlineGreek text={lesson.scene.intro} />
        </p>
        {!started && (
          <p className="muted">
            {round === 1
              ? `The ${role}'s lines play by themselves. When it's your turn, say your line aloud, then check it.`
              : 'Same scene, no English this time. Say each of your lines before the model does: you get about as long as it takes to say it.'}
          </p>
        )}
      </div>

      {started && (
        <div className="scene" aria-live="polite">
          {turns.slice(0, shown).map((turn, i) => {
            if (turn.who === 'narrator') {
              return (
                <p key={i} className="bubble bubble-narrator">
                  <InlineGreek text={turn.text} />
                </p>
              );
            }
            const item = getItem(turn.item);
            if (turn.who === 'staff') {
              const open = textShown.has(i);
              return (
                <div key={i} className="turn turn-staff">
                <Avatar cast={lesson.staff.cast} name={lesson.staff.role} size={32} active={i === current && !finished} />
                <div className="bubble bubble-staff">
                  <span className="bubble-who">
                    {lesson.staff.role}{turn.slow ? ' · slowly' : ''}
                    {turn.surprise ? ' · something new' : ''}
                  </span>
                  <div className="row" style={{ gap: 8 }}>
                    <button type="button" className="icon-btn" onClick={() => playTurn(turn)} aria-label={`Play the ${role}'s line again`}>
                      <Volume2 size={18} aria-hidden="true" />
                    </button>
                    {open ? (
                      <div className="stack" style={{ gap: 2 }}>
                        <GreekText text={item.text} size="body" />
                        {!turn.surprise && <span className="small muted">{item.meaning}</span>}
                      </div>
                    ) : (
                      <>
                        <SpokenLine clip={clipFor(item, sceneVoice(item, 'staff', lesson.staff))} />
                        <button type="button" className="btn btn-ghost small" style={{ paddingInline: 10 }} onClick={() => setTextShown((s) => new Set(s).add(i))}>
                          Show words
                        </button>
                      </>
                    )}
                  </div>
                  {turn.surprise && i === current && <p className="small muted">You haven't learned this one yet. What do you say?</p>}
                </div>
                </div>
              );
            }
            const done = revealed.has(i);
            return (
              <div key={i} className="turn turn-you">
              <div className="bubble bubble-you">
                <span className="bubble-who">You</span>
                {!done && round === 1 && (
                  <p>
                    <span className="small muted">Say: </span>
                    <strong>{item.meaning}</strong>
                  </p>
                )}
                {!done && round === 1 && i === current && canRecord && (
                  <RecordControl
                    inline
                    label="Record your line"
                    onResult={(r) => {
                      setTakes((t) => ({ ...t, [i]: r }));
                      reveal(i);
                    }}
                    onProblem={(problem) => {
                      noteMicProblem(problem);
                      setRefused(true);
                    }}
                  />
                )}
                {!done && round === 2 && (
                  <div className="row" style={{ gap: 10 }}>
                    {countdown && i === current && (
                      <svg className="countdown" viewBox="0 0 40 40" aria-hidden="true" style={{ ['--duration' as string]: `${fastSeconds(turn, lesson.staff)}s` }}>
                        <circle cx="20" cy="20" r="18" strokeDasharray="113" />
                      </svg>
                    )}
                    <span className="muted">Your line…</span>
                  </div>
                )}
                {done && (
                  <div className="stack reveal" style={{ gap: 4 }}>
                    <GreekText text={item.text} size="body" />
                    <span className="small muted">{item.meaning}</span>
                    <div className="row" style={{ gap: 8 }}>
                      <button type="button" className="btn btn-ghost small" onClick={() => playTurn(turn)} style={{ paddingInline: 8, minHeight: 36 }}>
                        <Play size={14} aria-hidden="true" fill="currentColor" /> Model
                      </button>
                      {takes[i] && (
                        <button
                          type="button"
                          className="btn btn-ghost small"
                          onClick={() => player.play(takes[i].url, { ...takes[i].speech, tag: `scene:${lesson.id}:you:${i}` })}
                          style={{ paddingInline: 8, minHeight: 36 }}
                        >
                          <Play size={14} aria-hidden="true" fill="currentColor" /> You
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <Avatar cast="you" name="You" size={32} active={i === current && !finished} />
              </div>
            );
          })}
          {finished && (
            <div className="panel-quiet reveal stack">
              <p className="section-title">{round === 1 ? 'Scene done' : 'Faster round done'}</p>
              <p>
                <InlineGreek text={lesson.scene.outro} />
              </p>
              {Object.keys(takes).length > 0 && (
                <button type="button" className="btn btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={playConversation}>
                  <Headphones size={18} aria-hidden="true" /> Hear your conversation
                </button>
              )}
            </div>
          )}
          <div ref={logEnd} className="scroll-anchor" />
        </div>
      )}

      <div className="actions">
        {!started && (
          <button type="button" className="btn btn-primary btn-block btn-lg" onClick={start}>
            <Play size={20} aria-hidden="true" fill="currentColor" /> {round === 1 ? 'Start the scene' : 'Start the faster round'}
          </button>
        )}
        {started && !finished && (
          <button type="button" className="btn btn-primary btn-block btn-lg" onClick={onNext}>
            {waitingForYou ? (
              <>
                <Eye size={20} aria-hidden="true" /> I said it, check
              </>
            ) : (
              <>
                Next line <ArrowRight size={20} aria-hidden="true" />
              </>
            )}
          </button>
        )}
        {finished && round === 1 && (
          <>
            <button type="button" className="btn btn-primary btn-block btn-lg" onClick={again}>
              <Repeat size={20} aria-hidden="true" /> Again, faster
            </button>
            <button type="button" className="btn btn-ghost btn-block" onClick={() => { player.stop(); onDone(); }}>
              Continue without the faster round
            </button>
          </>
        )}
        {finished && round === 2 && (
          <button type="button" className="btn btn-primary btn-block btn-lg" onClick={() => { player.stop(); onDone(); }}>
            Continue <ArrowRight size={20} aria-hidden="true" />
          </button>
        )}
        {!started && round === 1 && (
          <button type="button" className="btn btn-ghost btn-block" onClick={() => { player.stop(); onSkip(); }}>
            Skip the scene for now
          </button>
        )}
      </div>
    </div>
  );
}
